import React, { memo, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import ApiService from "../../Api/ApiServices";
import {
  ADMIN_ROLE,
  LOGIN_USER_ROLE_KEY,
  POWER_USER_ROLE,
  USER_ROLE_KEY,
} from "../../Constant";
import useAuth from "../../ContextProvider/AuthProvider";
import styled from "styled-components";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import useSWR from "swr";

const MemberDropDown = memo(function MemberDropDown() {
  const { setUserId, setUser, user } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const dropdownRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const loginUserRole = localStorage.getItem(LOGIN_USER_ROLE_KEY);
  const { t, i18n } = useTranslation();

  const { data: memberlist = [], isLoading } = useSWR(
    `getMemberslist`,
    async () => {
      try {
        const { data } = await ApiService.getMembers();
        if (data?.members?.length) {
          const filteredMembers = data.members?.map((item) => {
            return {
              label: `${item?.user_fname} ${item?.user_lname}`,
              value: item?.user_id,
            };
          });
          setMembers(data?.members);
          return filteredMembers || [];
        }
        return [];
      } catch (error) {
        console.error("error: ", error);
      }
    },
    {
      revalidateOnFocus: false,
    }
  );

  const handleMemberChange = async (e) => {
    const memberId = e;
    try {
      setUserId(memberId);
      const { data } = await ApiService.getSelfInfo(memberId);
      localStorage.setItem(USER_ROLE_KEY, data?.user_role);
      const userData = members?.find((x) => x?.user_id === memberId);
      setUser(userData);
    } catch (error) {}
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const selectMember = async (value) => {
    try {
      setUserId(value);
      const { data } = await ApiService.getSelfInfo(value);
      localStorage.setItem(USER_ROLE_KEY, data?.user_role);
      const userData = members?.find((x) => x?.user_id === value);
      setUser(userData);
      navigate("/user-information");
    } catch (error) {
      console.error("Error in selectMember:", error);
    }
    setDropdownOpen(false);
  };

  return (
    <>
      {(loginUserRole === ADMIN_ROLE || loginUserRole === POWER_USER_ROLE) && (
        <DropdownContainer className="dropdown" ref={dropdownRef}>
          {!isLoading && !memberlist?.length ? (
            <div
              style={{
                background: "#d9edf7",
                color: "#31708f",
                padding: "10px 20px",
              }}
            >
              <span>
                {t("screens.rightSidebar.text.noMembershipAssign")}
                <Link to="/myFootprintID">
                  {" "}
                  {t("screens.rightSidebar.text.gotoMyFootprint")}
                </Link>
              </span>
            </div>
          ) : (
            <>
              <DropdownToggle
                onClick={toggleDropdown}
              >
                <Image>
                  {user?.avatar ? (
                    <img src={user?.avatar} alt="logo" />
                  ) : (
                    <img src="/assets/images/profile.jpg" alt="logo" />
                  )}
                </Image>
                <ExpandMoreIcon style={{ color: '#666' }} />
              </DropdownToggle>
              <DropdownMenu
                className={`dropdown-menu ${dropdownOpen ? "show" : ""}`}
                role="menu"
                id="dropdownMenuShow"
              >
                {memberlist?.length > 0 ? (
                  memberlist?.map((item, index) => (
                    <DropdownItem 
                      key={index} 
                      className={`ng-scope ${item.value === user?.user_id ? 'selected' : ''}`}
                    >
                      <span
                        onClick={() => selectMember(item.value)}
                        className="ng-binding"
                      >
                        {item?.label}
                      </span>
                    </DropdownItem>
                  ))
                ) : (
                  <DropdownItem className="ng-scope">No Members Found</DropdownItem>
                )}
              </DropdownMenu>
            </>
          )}
        </DropdownContainer>
      )}
    </>
  );
});

export default MemberDropDown;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownToggle = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

const Image = styled.div`
  img {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
  }
`;

const DropdownMenu = styled.ul`
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 1000;
  min-width: 200px;
  padding: 8px 0;
  margin: 2px 0 0;
  background-color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  display: none;

  &.show {
    display: block;
  }
`;

const DropdownItem = styled.li`
  span {
    display: block;
    padding: 8px 16px;
    clear: both;
    font-weight: normal;
    color: #333;
    white-space: nowrap;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background-color: #f5f5f5;
      text-decoration: none;
    }
  }

  &.selected span {
    background-color: #e8f0fe;
    color: #1a73e8;
    font-weight: 500;
  }
`;
